import { useChat } from '@ai-sdk/react';
import { ActionIcon, Loader, ScrollArea, Textarea } from '@mantine/core';
import { IconSend, IconX } from '@tabler/icons-react';
import { DefaultChatTransport } from 'ai';
import classNames from 'classnames';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { getAgentToolResult } from 'src/Components/Agent/agent.utils';
import { AgentMessage } from 'src/Components/Agent/AgentMessage';
import { VoiceDictationButton } from 'src/Components/Shared/VoiceDictationButton';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { agentApi } from 'src/Services/API';
import {
  trackAgentContactCreated,
  trackAgentContactUpdated,
  trackAgentEventCreated,
  trackAgentEventDeleted,
  trackAgentEventUpdated,
  trackAgentDocumentShown,
  trackAgentDocumentsMoved,
  trackAgentDocumentsRenamed,
  trackAgentFolderCreated,
  trackAgentMessageSent,
  trackAgentTaskCreated,
  trackAgentTaskDeleted,
  trackAgentTaskUpdated,
} from 'src/Services/Mixpanel/events';

import styles from './AgentChat.module.scss';
import { Props } from './AgentChat.types';

const AgentChat = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [dictationError, setDictationError] = useState<string | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_URL}/assets/${assetId}/agent/chat`,
        credentials: 'include',
        // Lucien reads and writes times in the user's zone.
        body: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      }),
    [assetId]
  );

  const { messages, sendMessage, status, error } = useChat({
    id: assetId,
    transport,
    onFinish: ({ message }) => {
      const results = message.parts.flatMap(part => getAgentToolResult(part) ?? []);
      for (const result of results) {
        if (result.kind === 'contact') {
          queryClient.invalidateQueries(['fetch-contacts', assetId]);
          queryClient.invalidateQueries(['fetch-contact', assetId, result.contact.id]);
          if (result.action === 'created') {
            trackAgentContactCreated();
          } else {
            trackAgentContactUpdated();
          }
        } else if (result.kind === 'task') {
          queryClient.invalidateQueries(['tasks', assetId]);
          queryClient.invalidateQueries(['my-tasks']);
          if (result.action === 'created') {
            trackAgentTaskCreated();
          } else if (result.action === 'updated') {
            trackAgentTaskUpdated({ is_done: result.task.status === 'DONE' });
          } else {
            trackAgentTaskDeleted();
          }
        } else if (result.kind === 'document') {
          trackAgentDocumentShown({ is_folder: result.document.type === 'folder' });
        } else if (result.kind === 'folder' || result.kind === 'documents') {
          queryClient.invalidateQueries(['fetch-documents', assetId]);
          if (result.kind === 'folder') {
            trackAgentFolderCreated();
          } else if (result.action === 'moved') {
            trackAgentDocumentsMoved({ documents_count: result.count });
          } else {
            trackAgentDocumentsRenamed({ documents_count: result.count });
          }
        } else {
          queryClient.invalidateQueries(['fetch-events']);
          queryClient.invalidateQueries(['fetch-all-events', assetId]);
          queryClient.invalidateQueries(['fetch-event', result.event.id]);
          if (result.action === 'created') {
            trackAgentEventCreated({ is_recurring: Boolean(result.event.isRecurring) });
          } else if (result.action === 'updated') {
            trackAgentEventUpdated();
          } else {
            trackAgentEventDeleted({ scope: result.event.scope ?? 'series' });
          }
        }
      }
    },
  });

  const isBusy = status === 'submitted' || status === 'streaming';
  const lastMessage = messages[messages.length - 1];
  const lastPart = lastMessage?.parts[lastMessage.parts.length - 1];
  // Between tool calls and steps nothing streams: keep the indicator until Lucien is actually writing.
  const isWriting =
    lastMessage?.role === 'assistant' &&
    lastPart?.type === 'text' &&
    lastPart.state === 'streaming';

  useEffect(() => {
    viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight });
  }, [messages]);

  const send = () => {
    const text = input.trim();
    if (!text || isBusy) {
      return;
    }
    sendMessage({ text });
    trackAgentMessageSent({ messages_count: messages.length + 1 });
    setInput('');
    setDictationError(null);
  };

  // The dictated text is appended to what was already typed, to be read over before sending.
  const onTranscript = (text: string) => {
    setInput(previous => (previous.trim() ? `${previous.trimEnd()} ${text}` : text));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className={classNames(styles.container, onClose && styles.fullScreen)}>
      <div className={styles.header}>
        {t('agent.title')}
        {onClose && (
          <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label={t('agent.close')}>
            <IconX size={20} />
          </ActionIcon>
        )}
      </div>
      <ScrollArea className={styles.messages} viewportRef={viewportRef}>
        <div className={styles.messagesContent}>
          {messages.length === 0 && <p className={styles.empty}>{t('agent.empty')}</p>}
          {messages.map(message => (
            <AgentMessage key={message.id} message={message} />
          ))}
          {isBusy && !isWriting && (
            <div className={styles.working}>
              <Loader size="xs" />
              {t('agent.working')}
            </div>
          )}
          {error && <p className={styles.error}>{t('agent.error')}</p>}
        </div>
      </ScrollArea>
      {dictationError && (
        <p className={styles.dictationError} role="alert">
          {dictationError}
        </p>
      )}
      <div className={styles.footer}>
        <Textarea
          className={styles.input}
          value={input}
          onChange={event => setInput(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={t('agent.placeholder')}
          autosize
          maxRows={4}
        />
        {assetId && (
          <VoiceDictationButton
            // Inputs are forced to 48px high in lightTheme.
            size={48}
            transcribe={(audio, filename, language) =>
              agentApi.transcribe(assetId, audio, filename, language)
            }
            onTranscript={onTranscript}
            onError={setDictationError}
            module="agent"
          />
        )}
        <ActionIcon
          // Inputs are forced to 48px high in lightTheme.
          size={48}
          onClick={send}
          disabled={!input.trim() || isBusy}
          aria-label={t('agent.send')}
        >
          <IconSend size={18} />
        </ActionIcon>
      </div>
    </div>
  );
};

export default AgentChat;
